import { disableInvoiceMutation, enableInvoiceMutation } from "@/admin/accounts/accountsQuery.ts";
import type { GQAccountItem } from "@/admin/accounts/AccountsTable.tsx";
import { AdminModalButton, type AdminModalButtonProps } from "@/admin/AdminModalButton.tsx";
import { Missing } from "@/admin/AdminTable.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WModalContent } from "@/components/modal/WModal.tsx";
import { WTooltip } from "@/components/WTooltip.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { ECommonBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { accountLinkedMethodsAtom } from "@/modules/billing/BoundMethod.ts";
import { cn } from "@/utils/classNames.ts";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { useAtomValue } from "jotai/react";
import { useState } from "react";

interface PaymentMethodModalProps {
    item: GQAccountItem;
    value: ECommonBillingAccount;
}

interface PaymentMethodButtonProps extends PaymentMethodModalProps {
    label: AdminModalButtonProps["label"];
    isVerificationPending: boolean;
    isEnabled: boolean;
}

function MethodList({ value }: { value: ECommonBillingAccount }) {
    const linkedMethods = useAtomValue(accountLinkedMethodsAtom(value.account));
    if (linkedMethods.length === 0) {
        return <Missing />;
    }
    return linkedMethods.map((m) => (
        <div key={m.id} className={cn("text-nowrap", linkedMethods.length > 2 && "font-size-small")}>
            {/* <PaymentLinkIcon icon={m.method.id} />  */}
            {m.method.name}
        </div>
    ));
}

function PaymentMethodButton({ label, ...props }: PaymentMethodButtonProps) {
    return (
        <AdminModalButton label={label}>
            <PaymentMethodModalContent {...props} />
        </AdminModalButton>
    );
}

export function PaymentMethod({ item, value }: PaymentMethodModalProps) {
    const { account, hasPaymentMethodAttached, isInvoiceVerified, payType } = value;

    const inner = (() => {
        switch (payType) {
            case "invoice":
            case "invoice_deactive":
            case "invoice_pending": {
                let label;
                let tooltip;
                switch (payType) {
                    case "invoice":
                        label = "Invoice";
                        tooltip = "Disable invoice payment";
                        break;
                    case "invoice_pending":
                        label = <span className="text-warning">Invoice (pending)</span>;
                        tooltip = "Review invoice application";
                        break;
                    case "invoice_deactive":
                        label = <span className="text-error">Invoice (inactive)</span>;
                        tooltip = "Enable invoice payment";
                        break;
                }

                return (
                    <>
                        <WTooltip text={tooltip}>
                            <PaymentMethodButton
                                isEnabled={isInvoiceVerified}
                                isVerificationPending={payType === "invoice_pending"}
                                item={item}
                                label={label}
                                value={value}
                            />
                        </WTooltip>

                        {account.allow_debt && !account.send_invoice_email && (
                            <div className="color-error font-size-small whitespace-nowrap">Invoice emails disabled</div>
                        )}
                    </>
                );
            }
            case "card":
                if (!hasPaymentMethodAttached) {
                    return "-";
                }
                return <span>Credit Card</span>;
            case "prepay": {
                return <MethodList value={value} />;
            }
            default:
                exhaustiveSwitchCheck(payType);
        }
    })();
    return <div className={cn(account.is_deleted && "line-through")}>{inner}</div>;
}

function PaymentMethodModalContent({
    item,
    isEnabled,
    isVerificationPending,
}: Omit<PaymentMethodButtonProps, "label">) {
    const enableMutation = useStandardMutation(enableInvoiceMutation);
    const disableMutation = useStandardMutation(disableInvoiceMutation);

    const [enableInvoice, setEnableInvoice] = useState<boolean>(isVerificationPending ? false : !isEnabled);
    const { id } = item;

    async function onSubmit() {
        if (enableInvoice) {
            await enableMutation.mutateAsync({
                body: {
                    billing_account_id: Number(id),
                },
            });
        } else {
            await disableMutation.mutateAsync({
                body: {
                    billing_account_id: Number(id),
                },
            });
        }
    }

    function changeEnableInvoice(value: boolean) {
        setEnableInvoice(value);
    }

    const enableLabel = isVerificationPending ? "Accept" : "Enable";
    const disableLabel = isVerificationPending ? "Decline" : "Disable";
    const label = enableInvoice ? enableLabel : disableLabel;

    return (
        <WModalContent title="Invoice payment" label={label} modalAction={async () => await onSubmit()}>
            {isVerificationPending ? (
                <WCheckbox isSelected={enableInvoice} onChange={(value) => changeEnableInvoice(value)}>
                    Accept invoice payment for this account
                </WCheckbox>
            ) : (
                <p>{label} invoice payment.</p>
            )}
        </WModalContent>
    );
}

// cspell:words deactive
